import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useGetMaterials } from "hooks/useFormula";
import { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  CreateFormulaContentRequest,
  CreateFormulaRequest,
} from "types/formula";
import { Material } from "types/materials";

import EmptyMaterials from "./EmptyMaterials";
import FormulaInformation from "./FormulaInformation";
import LoadingMaterials from "./LoadingMaterials";

const TABLE_HEADERS = [
  { field: "Ingredient", headerName: "Ingredient" },
  { field: "unit", headerName: "Unit" },
  { field: "weight", headerName: "Weight" },
] as const;

// Layout constants
const LAYOUT_CONSTANTS = {
  TABLE_HEIGHT: 476,
  SUMMARY_WIDTH: 320,
  SUMMARY_MAX_HEIGHT: 620,
  CHECKBOX_WIDTH: 50,
  WEIGHT_FIELD_WIDTH: 200,
} as const;

interface FormulaCreatorProps {
  onCreate: (formula: CreateFormulaRequest) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
}

function FormulaCreator({
  onCreate,
  onClose,
  isCreating,
}: FormulaCreatorProps) {
  const { colors } = useCelleraTheme();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

  const [supply, setSupply] = useState<number>();

  const [selectedMaterials, setSelectedMaterials] = useState<Material[]>([]);
  const [materialWeights, setMaterialWeights] = useState<
    Record<string, string>
  >({});

  const { data: materialsResponse, isLoading: isLoadingMaterials } =
    useGetMaterials({
      page: page,
      pageSize: rowsPerPage,
      search: debouncedSearchTerm,
    });

  const materials = useMemo(() => {
    return materialsResponse?.items || [];
  }, [materialsResponse]);

  const pagination = useMemo(() => {
    return (
      materialsResponse?.pagination || {
        page: 0,
        pageSize: 0,
        total: 0,
        hasMore: false,
      }
    );
  }, [materialsResponse]);

  // Calculate select all checkbox state
  const selectedMaterialIds = useMemo(
    () => selectedMaterials.map((m) => m.id),
    [selectedMaterials]
  );

  const selectedOnPage = useMemo(
    () => materials.filter((m) => selectedMaterialIds.includes(m.id)),
    [materials, selectedMaterialIds]
  );

  const allSelectedOnPage =
    materials.length > 0 && selectedOnPage.length === materials.length;
  const someSelectedOnPage =
    selectedOnPage.length > 0 && selectedOnPage.length < materials.length;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1); // MUI uses 0-based, API uses 1-based
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page (1-based)
  };

  const handleSelectMaterial = useCallback(
    (material: Material) => {
      const isSelected = selectedMaterials.some((m) => m.id === material.id);

      if (isSelected) {
        // Remove material and clear its weight
        setSelectedMaterials((prev) =>
          prev.filter((m) => m.id !== material.id)
        );
        setMaterialWeights((prev) => {
          const newWeights = { ...prev };
          delete newWeights[material.id];
          return newWeights;
        });
      } else {
        // Add material with default min weight
        setSelectedMaterials((prev) => [...prev, material]);
        setMaterialWeights((prev) => ({
          ...prev,
          [material.id]: material.min,
        }));
      }
    },
    [selectedMaterials]
  );

  const handleSelectAll = useCallback(() => {
    const currentPageIds = materials.map((m) => m.id);

    if (allSelectedOnPage) {
      // Deselect all on current page
      setSelectedMaterials((prev) =>
        prev.filter((m) => !currentPageIds.includes(m.id))
      );
      setMaterialWeights((prev) => {
        const newWeights = { ...prev };
        currentPageIds.forEach((id) => {
          delete newWeights[id];
        });
        return newWeights;
      });
    } else {
      // Select all on current page
      const materialsToAdd = materials.filter(
        (material) => !selectedMaterialIds.includes(material.id)
      );

      setSelectedMaterials((prev) => [...prev, ...materialsToAdd]);
      setMaterialWeights((prev) => {
        const newWeights = { ...prev };
        materialsToAdd.forEach((material) => {
          newWeights[material.id] = material.min;
        });
        return newWeights;
      });
    }
  }, [materials, allSelectedOnPage, selectedMaterialIds]);

  const handleSupplyChange = (value: number) => {
    setSupply(value);
  };

  // Helper function to clamp value within material bounds
  const clampValue = useCallback((value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value));
  }, []);

  // Helper function to round value to nearest step
  const roundToStep = useCallback((value: number, step: number) => {
    return Math.round(value / step) * step;
  }, []);

  const handleWeightChange = useCallback((id: string, value: string) => {
    setMaterialWeights((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleWeightBlur = useCallback(
    (id: string, min: string, max: string, step: string) => {
      const currentValue = materialWeights[id];

      // If empty or undefined, set to min value
      if (!currentValue || currentValue === "") {
        setMaterialWeights((prev) => ({ ...prev, [id]: min }));
        return;
      }

      const numValue = parseFloat(currentValue);

      // If invalid, reset to min
      if (isNaN(numValue)) {
        setMaterialWeights((prev) => ({ ...prev, [id]: min }));
        return;
      }

      const minValue = parseFloat(min);
      const maxValue = parseFloat(max);
      const stepValue = parseFloat(step) || 1;

      // First clamp to range, then round to step
      const clampedValue = clampValue(numValue, minValue, maxValue);
      const roundedValue = roundToStep(clampedValue, stepValue);
      // Final clamp to ensure rounding didn't push outside bounds
      const finalValue = clampValue(roundedValue, minValue, maxValue);

      setMaterialWeights((prev) => ({
        ...prev,
        [id]: String(finalValue),
      }));
    },
    [materialWeights, clampValue, roundToStep]
  );

  const handleCreateFormula = useCallback(async () => {
    if (!supply || selectedMaterials.length === 0) {
      return;
    }

    // Map selected materials to formula contents with weights
    const validContents = selectedMaterials
      .map((material) => {
        const weight = materialWeights[material.id];
        const numWeight = parseFloat(weight);

        // Validate weight exists and is valid
        if (!weight || isNaN(numWeight) || numWeight <= 0) {
          return null;
        }

        return {
          materialId: material.id,
          weight: numWeight,
        };
      })
      .filter(
        (content): content is CreateFormulaContentRequest => content !== null
      );

    // Only proceed if we have valid contents
    if (validContents.length === 0) {
      return;
    }

    const params: CreateFormulaRequest = {
      supply,
      contents: validContents,
    };

    await onCreate(params);
    onClose();
  }, [supply, selectedMaterials, materialWeights, onCreate, onClose]);

  const handleCancel = () => {
    onClose();
  };

  const formatUnit = useCallback((material: Material) => {
    const step = material.step || "1";
    return `${material.min} - ${material.max} | ${step} ${material.unit} increments`;
  }, []);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      {/* Formula Information Fields */}
      <FormulaInformation supply={supply} onChangeSupply={handleSupplyChange} />

      <Box sx={{ display: "flex", gap: 1, flex: 1, minHeight: 0 }}>
        {/* Left Side - Ingredient Selection */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${colors.border.main}`,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Search and Filter Bar */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                gap: 2,
                borderBottom: `1px solid ${colors.border.main}`,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colors.text.secondary }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: colors.background.paper,
                  },
                }}
              />
            </Box>

            {/* Materials Table */}
            <TableContainer
              sx={{
                flex: 1,
                minHeight: LAYOUT_CONSTANTS.TABLE_HEIGHT,
                maxHeight: LAYOUT_CONSTANTS.TABLE_HEIGHT,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: colors.background.secondary,
                        borderRight: `1px solid ${colors.border.main}`,
                        width: LAYOUT_CONSTANTS.CHECKBOX_WIDTH,
                      }}
                    >
                      <Checkbox
                        checked={allSelectedOnPage}
                        indeterminate={someSelectedOnPage}
                        onChange={handleSelectAll}
                        sx={{ py: 0.5 }}
                      />
                    </TableCell>
                    {TABLE_HEADERS.map((header) => (
                      <TableCell
                        key={header.field}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: colors.background.secondary,
                          borderRight: `1px solid ${colors.border.main}`,
                        }}
                      >
                        {header.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingMaterials ? (
                    <LoadingMaterials />
                  ) : materials.length === 0 ? (
                    <EmptyMaterials />
                  ) : (
                    materials.map((material) => (
                      <TableRow
                        key={material.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: colors.background.secondary,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            borderRight: `1px solid ${colors.border.main}`,
                          }}
                        >
                          <Checkbox
                            checked={selectedMaterialIds.includes(material.id)}
                            onChange={() => handleSelectMaterial(material)}
                            sx={{ py: 0.5 }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            borderRight: `1px solid ${colors.border.main}`,
                          }}
                          onClick={() => handleSelectMaterial(material)}
                        >
                          {material.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            borderRight: `1px solid ${colors.border.main}`,
                          }}
                          onClick={() => handleSelectMaterial(material)}
                        >
                          {formatUnit(material)}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.5,
                            width: LAYOUT_CONSTANTS.WEIGHT_FIELD_WIDTH,
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={materialWeights[material.id] || ""}
                            onChange={(e) => {
                              handleWeightChange(material.id, e.target.value);
                            }}
                            onBlur={() =>
                              handleWeightBlur(
                                material.id,
                                material.min,
                                material.max,
                                material.step
                              )
                            }
                            slotProps={{
                              input: {
                                inputProps: {
                                  step: material.step,
                                  min: material.min,
                                  max: material.max,
                                  style: {
                                    fontSize: 14,
                                  },
                                  disabled: !selectedMaterialIds.includes(
                                    material.id
                                  ),
                                },
                              },
                            }}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: colors.border.light,
                                },
                                "&:hover fieldset": {
                                  borderColor: colors.border.main,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.secondary.main,
                                },
                              },
                              "& input": {
                                py: 0.75,
                                px: 1.2,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                borderTop: `1px solid ${colors.border.main}`,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                px: 2,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={pagination.total}
                rowsPerPage={rowsPerPage}
                page={page - 1} // Convert to 0-based for MUI
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  `Results: ${from}-${to} of ${count}`
                }
                sx={{
                  border: "none",
                  ".MuiTablePagination-toolbar": {
                    paddingLeft: 0,
                  },
                  ".MuiTablePagination-displayedRows": {
                    color: colors.text.secondary,
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Right Side - Formula Summary */}
        <Box
          sx={{
            width: LAYOUT_CONSTANTS.SUMMARY_WIDTH,
            flexShrink: 0,
            maxHeight: LAYOUT_CONSTANTS.SUMMARY_MAX_HEIGHT,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${colors.border.main}`,
              borderRadius: 2,
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${colors.border.main}`,
                backgroundColor: colors.background.secondary,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                Formula
              </Typography>
            </Box>

            {/* Selected Ingredients List */}
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              {selectedMaterials.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "200px",
                    px: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      textAlign: "center",
                    }}
                  >
                    No ingredients selected. Select ingredients from the list to
                    build your formula.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  {selectedMaterials.map((item) => (
                    <Box key={item.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            minWidth: 50,
                            textAlign: "right",
                          }}
                        >
                          {materialWeights[item.id]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.border.main}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            borderColor: colors.border.main,
            color: colors.text.primary,
            "&:hover": {
              borderColor: colors.border.dark,
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateFormula}
          sx={{
            textTransform: "none",
            backgroundColor: colors.secondary.main,
            "&:hover": {
              backgroundColor: colors.secondary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
          disabled={!supply || selectedMaterials.length === 0 || isCreating}
          loading={isCreating}
        >
          Create Formula
        </Button>
      </Box>
    </Box>
  );
}

export default FormulaCreator;
